import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { OrderStatus } from "types/order-status";

interface UpdateOrderStatusRequest {
  orderId: string;
  newStatus: OrderStatus;
}

export const useOrderActions = () => {
  const { client, session } = useAuthContext();
  const queryClient = useQueryClient();

  const updateOrderStatus = async ({
    orderId,
    newStatus,
  }: UpdateOrderStatusRequest) => {
    if (!client) throw new Error("useOrderActions: Client not initialized");
    if (!session) throw new Error("useOrderActions: No active session");

    const { data, error } = await client.functions.invoke(
      "admin-update-order-status",
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
        body: { orderId, newStatus },
      }
    );

    if (error) throw error;
    return data.order;
  };

  const mutation = useMutation({
    mutationFn: updateOrderStatus,
    onSuccess: (data, variables) => {
      // Invalidate the orders list query
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
      // Invalidate the specific order query to update the detail page
      queryClient.invalidateQueries({ queryKey: ["admin-order", variables.orderId] });
    },
  });

  const approveOrder = (orderId: string) => {
    return mutation.mutateAsync({ orderId, newStatus: "awaiting_submission" });
  };

  const rejectOrder = (orderId: string) => {
    return mutation.mutateAsync({ orderId, newStatus: "rejected" });
  };

  const updateStatus = (orderId: string, newStatus: OrderStatus) => {
    return mutation.mutateAsync({ orderId, newStatus });
  };

  return {
    approveOrder,
    rejectOrder,
    updateOrderStatus: updateStatus,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
  };
};
